import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import i18n from '../../../assets/languages/configs';
import EmptyData from '../../../components/empty-data/EmptyData';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import SelectNumber from '../../../components/SelectNumber/SelectNumber';
import GooglePayment from '../../../components/GooglePayment/GooglePayment';
import ApplePayment from '../../../components/ApplePayment/ApplePayment';
import BuyComboItem from '../../../components/BuyComboItem/BuyComboItem';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import AvailableWeekdays from '../../../components/AvailableWeekdays/AvailableWeekdays';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';

import { useFujiAuth } from '../../../auth/useFujiAuth';
import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';
import useTicketInfo from '../../../hooks/useTicketInfo';
import {
    IS_IN_PURCHASE,
    PAYMENT_TYPE,
    PRICE_TYPE,
    TICKET_TYPE,
    VOUCHER_TYPE,
    localStorageKeys,
} from '../../../utils/Constants';

import {
    calcTotalPrice,
    convertTextToLinks,
    getValueByLanguage,
    isOnSaleTicket,
    showNotificationDialog,
    isEmptyObject,
    hasSelectedNumber,
    isComboTicket,
    isReserveTicket,
} from '../../../utils';
import { LocalStorageManager } from '../../../utils/LocalStorageManager/LocalStorageManager';

import styles from './styles.module.css';
import { SelectNumber as SelectNumberT, MultilingualType } from 'types';

/**
 * チケット購入画面
 */
const BuyTicketInfoScreen = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language } = i18n;
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { formatMoney, currencyUnit } = useLocalizedMoney();
    const { user, isAuthenticated } = useFujiAuth();
    const [selectNumber, setSelectNumber] = useState<SelectNumberT | null>(null);
    const [customDefaultNumber, setCustomDefaultNumber] = useState<SelectNumberT | null>(null);

    const { isLoading: isFetchingTicketInfo, ticketInfo, fetchTicketInfo } = useTicketInfo();
    const {
        isLoading: isTicketInPurchase,
        purchaseWithPayPay,
        hasEmailInUserInfo,
        EmailConfirmModal,
        purchaseWithNoCost,
        WarningConfirmModal,
    } = usePurchaseTicket();
    const location = useLocation();
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const { anonymousFormAnswers = null } = location.state ?? {};
    const storageManager = useMemo(() => new LocalStorageManager('BuyTicketInfoScreen'), []);

    const hasVoucherSupport = useMemo(
        () =>
            ticketInfo?.voucherType === VOUCHER_TYPE.FIXED_FIRST ||
            ticketInfo?.voucherType === VOUCHER_TYPE.ONE_TIME_FIRST,
        [ticketInfo?.voucherType],
    );

    const shouldRedirectToVoucherScreen = useMemo(
        () => anonymousFormAnswers == null && (ticketInfo?.isDisplayAnonymousForm === true || hasVoucherSupport),
        [anonymousFormAnswers, ticketInfo?.isDisplayAnonymousForm, hasVoucherSupport],
    );

    const voucherCode = useMemo(() => searchParams.get('voucherCode'), [searchParams]);

    const comboItem = useMemo(() => ticketInfo?.comboItems[0], [ticketInfo?.comboItems]);

    // 初回レンダリング
    useEffect(() => {
        const localTicketInfo = storageManager.getStorage(localStorageKeys.TICKET_INFO_DATA);
        let salesTicketId;
        if (localTicketInfo?.salesTicketId) {
            salesTicketId = localTicketInfo.salesTicketId ?? null;
            setCustomDefaultNumber(localTicketInfo.selectNumber);
        }

        // get parameterのticketIdを優先する。
        if (searchParams.get('salesTicketId')) {
            salesTicketId = searchParams.get('salesTicketId');
            if (String(localTicketInfo?.salesTicketId) !== String(salesTicketId)) {
                setCustomDefaultNumber(null);
                localStorage.removeItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS);
                localStorage.removeItem(localStorageKeys.TICKET_INFO_DATA);
            }
        }
        if (!salesTicketId) {
            navigate('/buy');
            return;
        } else {
            void fetchTicketInfo(salesTicketId);
        }
    }, [navigate, fetchTicketInfo, searchParams, storageManager]);

    useEffect(() => {
        if (!ticketInfo) {
            return;
        }
        if (isReserveTicket(ticketInfo)) {
            const path = voucherCode
                ? `/buy/reservation?salesTicketId=${ticketInfo?.salesTicketId}&voucherCode=${voucherCode}`
                : `/buy/reservation?salesTicketId=${ticketInfo?.salesTicketId}`;
            navigate(path, { replace: true });
        }
    }, [ticketInfo, navigate, voucherCode]);

    const totalSelectNumber = useMemo(() => {
        if (!selectNumber) {
            return 0;
        }
        return Object.values(selectNumber)
            .filter((value) => value !== undefined && value !== null)
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    }, [selectNumber]);

    // 購入ボタン押下時の挙動
    const handlePaymentClick = () => {
        if (!hasSelectedNumber(selectNumber)) {
            showNotificationDialog(
                'error',
                'selectNumberError',
                t('notification.title'),
                t('validate.minimum-one-person'),
            );
            return;
        }
        if (!hasEmailInUserInfo()) {
            return;
        }
        navigate('credit-payment', {
            state: { ticketInfo: ticketInfo, selectNumber: selectNumber, anonymousFormAnswers },
        });
    };

    // 購入ボタン押下時の挙動
    const handlePayPayClick = () => {
        if (!hasSelectedNumber(selectNumber)) {
            showNotificationDialog(
                'error',
                'selectNumberError',
                t('notification.title'),
                t('validate.minimum-one-person'),
            );
            return;
        }
        if (!hasEmailInUserInfo() || !selectNumber) {
            return;
        }
        void purchaseWithPayPay({
            salesTicketId: ticketInfo?.salesTicketId,
            paymentType: PAYMENT_TYPE.paypay,
            number: selectNumber,
            reservationCombo: null,
            userForm: null,
            anonymousFormAnswers,
        });
    };

    // ログインして購入を押下時
    const handleLoginClick = () => {
        const path = voucherCode
            ? `/buy/ticket?salesTicketId=${ticketInfo?.salesTicketId}&voucherCode=${voucherCode}`
            : `/buy/ticket?salesTicketId=${ticketInfo?.salesTicketId}`;

        localStorage.setItem(IS_IN_PURCHASE, 'true');
        localStorage.setItem(localStorageKeys.LOGIN_FLOW, JSON.stringify({ path, state: {} }));
        navigate('/auth');
    };

    // 更新ボタン押下時
    const handleRefetch = () => {
        void fetchTicketInfo(searchParams.get('salesTicketId') ?? '');
    };

    const handleChangeNumber = useCallback(
        (newNumber: SelectNumberT | null) => {
            const localTicketInfo = storageManager.getStorage(localStorageKeys.TICKET_INFO_DATA) ?? {};
            storageManager.setStorage(localStorageKeys.TICKET_INFO_DATA, {
                ...localTicketInfo,
                selectNumber: newNumber,
            });
            setSelectNumber(newNumber);
        },
        [storageManager],
    );

    const handleNextStep = () => {
        if (!hasSelectedNumber(selectNumber)) {
            showNotificationDialog(
                'error',
                'selectNumberError',
                t('notification.title'),
                t('validate.minimum-one-person'),
            );
            return;
        }
        localStorage.setItem(
            localStorageKeys.RESERVATION_INFO_DATA,
            JSON.stringify({
                ticketInfo: ticketInfo,
                selectNumber: selectNumber,
            }),
        );
        const path = hasVoucherSupport && voucherCode ? `/buy/vouchers?voucherCode=${voucherCode}` : '/buy/vouchers';
        navigate(path);
    };

    const handlePurchaseNoCostTicket = async () => {
        if (!selectNumber) {
            return;
        }
        void purchaseWithNoCost({
            salesTicketId: ticketInfo?.salesTicketId,
            number: selectNumber,
            anonymousFormAnswers,
            userForm: null,
            reservationCombo: null,
            email: user?.email ?? null,
        });
    };

    if (!ticketInfo) {
        return <CenterLoading />;
    }

    const { isDisplayRemainingCount, maxParticipantCount, currentParticipantCount } = ticketInfo;

    const hasParticipantCount = maxParticipantCount != null;
    const isDisplayRemaining = isDisplayRemainingCount && hasParticipantCount;
    const remainingCount = hasParticipantCount ? maxParticipantCount - (currentParticipantCount ?? 0) : -1;

    const calculateCouponCount = (couponCount?: number | null, selectedNumber?: number | null) => {
        return (couponCount ?? 0) * (selectedNumber ?? 0);
    };

    return (
        <>
            <Header title={t('header.buy-ticket')} refetch={handleRefetch} />
            {(isFetchingTicketInfo || isTicketInPurchase) && <CenterLoading />}
            <EmailConfirmModal />
            <WarningConfirmModal />
            <div className="content-container scrollable">
                {navigator.onLine && !isEmptyObject(ticketInfo) && ticketInfo != null ? (
                    <div className={styles.wrapper}>
                        {/* チケットの情報 */}
                        <div className={styles.ticketInfo}>
                            <h4 className={styles.ticketName}>
                                <TextBreaks text={l(ticketInfo?.ticketName)} />
                            </h4>
                            <p className={styles.descriptions}>
                                <TextBreaks text={l(ticketInfo?.subtitle)} />
                            </p>
                            {!ticketInfo?.isCombination && comboItem?.ticketType === TICKET_TYPE.COUPON && (
                                <p className={styles.descriptions}>
                                    {ticketInfo?.isInUseAdult && comboItem?.isInUseAdult && (
                                        <span className={styles.couponItem}>
                                            {t('ticket-detail.adult') +
                                                ': ' +
                                                t('ticket-detail.coupon-count', {
                                                    count: calculateCouponCount(
                                                        comboItem?.couponCount,
                                                        selectNumber?.adult,
                                                    ),
                                                })}
                                        </span>
                                    )}
                                    {ticketInfo?.isInUseCustom1 && comboItem?.isInUseCustom1 && (
                                        <span className={styles.couponItem}>
                                            {l(ticketInfo.custom1Name) +
                                                ': ' +
                                                t('ticket-detail.coupon-count', {
                                                    count: calculateCouponCount(
                                                        comboItem?.couponCount,
                                                        selectNumber?.custom1,
                                                    ),
                                                })}
                                        </span>
                                    )}
                                    {ticketInfo?.isInUseCustom2 && comboItem?.isInUseCustom2 && (
                                        <span className={styles.couponItem}>
                                            {l(ticketInfo.custom2Name) +
                                                ': ' +
                                                t('ticket-detail.coupon-count', {
                                                    count: calculateCouponCount(
                                                        comboItem?.couponCount,
                                                        selectNumber?.custom2,
                                                    ),
                                                })}
                                        </span>
                                    )}
                                    {ticketInfo?.isInUseCustom3 && comboItem?.isInUseCustom3 && (
                                        <span className={styles.couponItem}>
                                            {l(ticketInfo.custom3Name) +
                                                ': ' +
                                                t('ticket-detail.coupon-count', {
                                                    count: calculateCouponCount(
                                                        comboItem?.couponCount,
                                                        selectNumber?.custom3,
                                                    ),
                                                })}
                                        </span>
                                    )}
                                </p>
                            )}
                            {/*使用開始可能曜日*/}
                            {!isComboTicket(ticketInfo) && (
                                <AvailableWeekdays
                                    ticket={{ ...ticketInfo, ...ticketInfo?.comboItems[0] }}
                                    className={styles.descriptions}
                                />
                            )}
                            {isOnSaleTicket(ticketInfo) && ticketInfo?.availableDeadlineAt && (
                                <p className={styles.descriptions}>
                                    {t('ticket-detail.activate-deadline') +
                                        ': ' +
                                        formatTime(ticketInfo?.availableDeadlineAt, 'll')}
                                </p>
                            )}
                            {isDisplayRemaining && remainingCount >= 0 && isOnSaleTicket(ticketInfo) && (
                                <div className={styles.descriptions}>
                                    {`${t('reservation.residue')}: ${remainingCount}`}
                                </div>
                            )}
                        </div>

                        {ticketInfo?.isCombination && (
                            <BuyComboItem
                                comboItem={ticketInfo?.comboItems}
                                selectNumber={selectNumber}
                                ticketInfo={ticketInfo}
                            />
                        )}
                        {/* 人数を選択 */}
                        <div className={styles.personPicker}>
                            <SelectNumber
                                ticketInfo={ticketInfo}
                                onChange={handleChangeNumber}
                                customDefaultNumber={customDefaultNumber}
                            />
                        </div>
                        {/* 合計金閣 */}
                        <div className={styles.totalPrice}>
                            {t('ticket-detail.total-amount')}
                            <span className={styles.price}>
                                {formatMoney(
                                    calcTotalPrice(selectNumber, {
                                        adult: ticketInfo?.priceAdult ?? 0,
                                        custom1: ticketInfo?.priceCustom1 ?? 0,
                                        custom2: ticketInfo?.priceCustom2 ?? 0,
                                        custom3: ticketInfo?.priceCustom3 ?? 0,
                                    }),
                                )}
                            </span>
                            {currencyUnit('JPY')}
                            <span className={styles.taxIncluded}>{t('ticket-detail.tax-included')}</span>
                        </div>
                        {ticketInfo?.note && (
                            <div className={styles.ticketInfo}>
                                <p className={styles.textNote}>{convertTextToLinks(l(ticketInfo?.note))}</p>
                            </div>
                        )}
                        <div className={styles.btnArea}>
                            {isOnSaleTicket(ticketInfo) &&
                            (remainingCount === -1 || remainingCount >= totalSelectNumber) ? (
                                <>
                                    {isAuthenticated ? (
                                        <>
                                            {!shouldRedirectToVoucherScreen ? (
                                                ticketInfo?.priceType !== PRICE_TYPE.NO_COST ? (
                                                    <>
                                                        <p>{t('ticket-detail.paymentType.before')}</p>
                                                        <CustomAntButton
                                                            className={styles.paymentBtn}
                                                            block
                                                            onClick={handlePaymentClick}
                                                            type="payment"
                                                        >
                                                            {t('ticket-detail.paymentType.credit-card')}
                                                        </CustomAntButton>
                                                        <GooglePayment
                                                            ticketInfo={ticketInfo}
                                                            selectNumber={selectNumber}
                                                            anonymousFormAnswers={anonymousFormAnswers}
                                                        />
                                                        <ApplePayment
                                                            ticketInfo={ticketInfo}
                                                            selectNumber={selectNumber}
                                                            anonymousFormAnswers={anonymousFormAnswers}
                                                            email={user.email}
                                                        />
                                                        <CustomAntButton
                                                            className={styles.paymentBtn}
                                                            block
                                                            onClick={handlePayPayClick}
                                                            type="payment"
                                                        >
                                                            {t('ticket-detail.paymentType.paypay')}
                                                        </CustomAntButton>
                                                    </>
                                                ) : (
                                                    <CustomAntButton
                                                        className={styles.coloredBtn}
                                                        block
                                                        onClick={handlePurchaseNoCostTicket}
                                                    >
                                                        {t('button.get-ticket')}
                                                    </CustomAntButton>
                                                )
                                            ) : (
                                                <CustomAntButton
                                                    className={styles.nextStepBtn}
                                                    block
                                                    onClick={handleNextStep}
                                                    type="outlined"
                                                >
                                                    {t('reservation.next-step')}
                                                </CustomAntButton>
                                            )}
                                        </>
                                    ) : (
                                        <CustomAntButton className={styles.coloredBtn} block onClick={handleLoginClick}>
                                            {t('ticket-detail.login-and-purchase')}
                                        </CustomAntButton>
                                    )}
                                </>
                            ) : (
                                <CustomAntButton className={styles.disableBtn} block disabled type="disabled">
                                    {t(
                                        hasParticipantCount &&
                                            remainingCount <= totalSelectNumber &&
                                            isOnSaleTicket(ticketInfo)
                                            ? 'reservation.sold-out'
                                            : 'button.not-on-sale',
                                    )}
                                </CustomAntButton>
                            )}
                        </div>
                    </div>
                ) : (
                    !isFetchingTicketInfo && <EmptyData />
                )}
            </div>
        </>
    );
};

export default memo(BuyTicketInfoScreen);
